// Wochenplan.
//
// Neu: freie Wochennavigation (vorher fix −3…+6 Tage), mehrere Mahlzeiten pro
// Tag mit Typ, Verschieben per Ziehen, Portionen abweichend vom Rezept und
// „Woche füllen" aus dem, was ohnehin bald ablaufen würde.
import { MEAL_TYPES, WEEKDAYS_LONG, localDate, startOfWeek } from '@speis/shared';
import type { PlanEntryView, RecipeView } from '@speis/shared';
import { api } from '../core/api.js';
import { guard, guarded } from '../core/guard.js';
import { onLiveEvent } from '../core/sse.js';
import { state } from '../core/store.js';
import { clear, el, haptic } from '../ui/dom.js';
import { confirmDialog } from '../ui/dialog.js';
import { icon } from '../ui/icons.js';
import { openSheet } from '../ui/sheet.js';
import { toast } from '../ui/toast.js';
import { skeletonList } from './shared.js';

let weekOffset = 0;
let host: HTMLElement | null = null;
let unsubscribe: (() => void) | null = null;
let plan: PlanEntryView[] = [];
let recipes: RecipeView[] = [];

const STATUS: Record<string, { label: string; cls: string }> = {
  planned: { label: 'geplant', cls: 'badge-info' },
  cooked: { label: 'gekocht', cls: 'badge-brand' },
  skipped: { label: 'übersprungen', cls: 'badge' },
};

export function renderPlan(container: HTMLElement): void {
  host = container;
  unsubscribe?.();
  clear(container);
  container.append(skeletonList(5));
  void load();
  unsubscribe = onLiveEvent('plan', () => void load());
}

export function teardownPlan(): void {
  unsubscribe?.();
  unsubscribe = null;
  host = null;
}

function weekDays(): Date[] {
  const monday = startOfWeek();
  monday.setDate(monday.getDate() + weekOffset * 7);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

async function load(): Promise<void> {
  const days = weekDays();
  const from = localDate(days[0]!);
  const to = localDate(days[6]!);

  const [planResult, recipeResult] = await Promise.all([
    guard(() => api<{ plan: PlanEntryView[] }>(`/api/plan?from=${from}&to=${to}`), {
      failure: 'Plan konnte nicht geladen werden',
    }),
    recipes.length
      ? Promise.resolve({ recipes })
      : guard(() => api<{ recipes: RecipeView[] }>('/api/recipes'), { failure: 'Rezepte' }),
  ]);

  plan = planResult?.plan ?? [];
  recipes = recipeResult?.recipes ?? recipes;
  draw();
}

function draw(): void {
  if (!host) return;
  clear(host);

  const days = weekDays();
  host.append(weekHeader(days));

  const today = localDate();
  for (const day of days) {
    host.append(dayCard(day, today));
  }

  host.append(
    el('div', { class: 'plan-tools' }, [
      el(
        'button',
        {
          class: 'btn btn-secondary btn-block',
          type: 'button',
          onclick: guarded(
            async () => {
              await api('/api/shopping/refresh-auto', { method: 'POST', body: {} });
            },
            { success: 'Einkaufsliste aus Plan & Meldebestand aktualisiert' },
          ),
        },
        [icon('refresh', { size: 18 }), 'Einkaufsliste abgleichen'],
      ),
    ]),
  );
}

function weekHeader(days: Date[]): HTMLElement {
  const first = days[0]!;
  const last = days[6]!;
  const fmt = (d: Date) => `${d.getDate()}.${d.getMonth() + 1}.`;
  const title =
    weekOffset === 0
      ? 'Diese Woche'
      : weekOffset === 1
        ? 'Nächste Woche'
        : `${fmt(first)} – ${fmt(last)}`;

  return el('div', { class: 'week-nav' }, [
    el(
      'button',
      {
        class: 'icon-btn',
        type: 'button',
        'aria-label': 'Vorherige Woche',
        onclick: () => {
          weekOffset--;
          void load();
        },
      },
      icon('chevronLeft'),
    ),
    el('div', { class: 'week-title' }, [
      el('strong', {}, title),
      el('span', { class: 'tiny' }, `${fmt(first)} – ${fmt(last)}`),
    ]),
    el(
      'button',
      {
        class: 'icon-btn',
        type: 'button',
        'aria-label': 'Nächste Woche',
        onclick: () => {
          weekOffset++;
          void load();
        },
      },
      icon('chevronRight'),
    ),
    weekOffset !== 0
      ? el(
          'button',
          {
            class: 'btn btn-sm btn-ghost',
            type: 'button',
            onclick: () => {
              weekOffset = 0;
              void load();
            },
          },
          'Heute',
        )
      : null,
  ]);
}

function dayCard(day: Date, today: string): HTMLElement {
  const iso = localDate(day);
  const entries = plan.filter((p) => p.date === iso);
  const isToday = iso === today;
  const isPast = iso < today;

  const card = el(
    'section',
    {
      class: `day-card${isToday ? ' is-today' : ''}${isPast ? ' is-past' : ''}`,
      'data-date': iso,
    },
    [
      el('header', { class: 'day-head' }, [
        el('div', {}, [
          el('strong', {}, isToday ? 'Heute' : WEEKDAYS_LONG[day.getDay()]!),
          el('span', { class: 'tiny' }, ` ${day.getDate()}.${day.getMonth() + 1}.`),
        ]),
        el(
          'button',
          {
            class: 'icon-btn',
            type: 'button',
            'aria-label': `Gericht für ${WEEKDAYS_LONG[day.getDay()]} einplanen`,
            onclick: () => openRecipePicker(iso),
          },
          icon('plus', { size: 18 }),
        ),
      ]),
    ],
  );

  if (!entries.length) {
    card.append(el('p', { class: 'day-empty tiny' }, 'nichts geplant'));
  } else {
    for (const entry of entries) card.append(planRow(entry));
  }

  // Ziel für Drag & Drop zwischen Tagen.
  card.addEventListener('dragover', (e) => {
    e.preventDefault();
    card.classList.add('is-droptarget');
  });
  card.addEventListener('dragleave', () => card.classList.remove('is-droptarget'));
  card.addEventListener('drop', (e) => {
    e.preventDefault();
    card.classList.remove('is-droptarget');
    const id = Number((e as DragEvent).dataTransfer?.getData('text/plain'));
    if (id) void movePlan(id, iso);
  });

  return card;
}

function planRow(entry: PlanEntryView): HTMLElement {
  const status = STATUS[entry.status] ?? STATUS.planned!;
  const meal = MEAL_TYPES.find((m) => m.key === entry.mealType);

  const row = el('div', { class: 'plan-entry', draggable: entry.status === 'planned' }, [
    el('div', { class: 'item-body' }, [
      el('div', { class: 'item-name' }, entry.recipeName),
      el('div', { class: 'item-meta' }, [
        meal ? el('span', {}, `${meal.icon} ${meal.label}`) : null,
        el('span', {}, `${entry.servings} P.`),
        el('span', { class: `badge ${status.cls}` }, status.label),
      ]),
    ]),
    el('div', { class: 'row-actions' }, [
      entry.status !== 'cooked'
        ? el(
            'button',
            {
              class: 'btn btn-sm btn-primary',
              type: 'button',
              onclick: () => void setStatus(entry, 'cooked'),
            },
            'gekocht',
          )
        : null,
      el(
        'button',
        {
          class: 'icon-btn',
          type: 'button',
          'aria-label': 'Weitere Optionen',
          onclick: () => openEntrySheet(entry),
        },
        icon('chevronDown', { size: 18 }),
      ),
    ]),
  ]);

  row.addEventListener('dragstart', (e) => {
    (e as DragEvent).dataTransfer?.setData('text/plain', String(entry.id));
    row.classList.add('is-dragging');
  });
  row.addEventListener('dragend', () => row.classList.remove('is-dragging'));

  return row;
}

async function movePlan(id: number, date: string): Promise<void> {
  await guard(
    async () => {
      await api(`/api/plan/${id}`, { method: 'PATCH', body: { date } });
      await load();
    },
    { failure: 'Verschieben fehlgeschlagen' },
  );
}

async function setStatus(entry: PlanEntryView, status: 'cooked' | 'skipped'): Promise<void> {
  haptic(12);
  const result = await guard(
    () =>
      api<{ cooked: { shortfalls: { name: string }[] } | null }>(`/api/plan/${entry.id}`, {
        method: 'PATCH',
        body: { status },
      }),
    { failure: 'Konnte nicht buchen' },
  );
  if (!result) return;

  if (status === 'cooked') {
    const short = result.cooked?.shortfalls ?? [];
    toast(
      short.length
        ? `Gekocht – knapp war: ${short.map((s) => s.name).join(', ')}`
        : 'Gekocht & abgebucht',
    );
  } else {
    toast('Übersprungen – nichts abgebucht');
  }
  await load();
}

function openEntrySheet(entry: PlanEntryView): void {
  const servings = el('input', {
    class: 'input',
    type: 'number',
    min: 1,
    max: 50,
    value: entry.servings,
  });

  const meal = el('select', { class: 'select' });
  for (const m of MEAL_TYPES) {
    const option = el('option', { value: m.key }, `${m.icon} ${m.label}`);
    if (m.key === entry.mealType) option.selected = true;
    meal.append(option);
  }

  const body = el('div', {}, [
    el('p', { class: 'muted' }, entry.recipeName),
    el('div', { class: 'field' }, [el('label', {}, 'Mahlzeit'), meal]),
    el('div', { class: 'field' }, [el('label', {}, 'Portionen'), servings]),
    el(
      'p',
      { class: 'field-hint' },
      'Die Portionszahl bestimmt, wie viel beim Kochen abgebucht wird.',
    ),
  ]);

  const sheet = openSheet(body, { title: 'Eintrag' });

  sheet.addActions(
    el(
      'button',
      {
        class: 'btn btn-primary btn-block',
        type: 'button',
        onclick: guarded(
          async () => {
            await api(`/api/plan/${entry.id}`, {
              method: 'PATCH',
              body: { mealType: meal.value, servings: Number(servings.value) || entry.servings },
            });
            sheet.close();
            await load();
          },
          { success: 'Gespeichert' },
        ),
      },
      'Speichern',
    ),
    entry.status === 'planned'
      ? el(
          'button',
          {
            class: 'btn btn-secondary btn-block',
            type: 'button',
            onclick: () => {
              sheet.close();
              void setStatus(entry, 'skipped');
            },
          },
          'Überspringen (kein Abzug)',
        )
      : null,
    el(
      'button',
      {
        class: 'btn btn-danger btn-block',
        type: 'button',
        onclick: async () => {
          const ok = await confirmDialog({
            title: 'Eintrag entfernen?',
            confirmLabel: 'Entfernen',
            danger: true,
          });
          if (!ok) return;
          sheet.close();
          await guard(
            async () => {
              await api(`/api/plan/${entry.id}`, { method: 'DELETE' });
              await load();
            },
            { success: 'Entfernt' },
          );
        },
      },
      'Entfernen',
    ),
  );
}

function openRecipePicker(date: string): void {
  if (!recipes.length) {
    toast('Erst ein Rezept anlegen – Reiter „Rezepte"');
    return;
  }

  const search = el('input', { class: 'input', type: 'search', placeholder: 'Rezept suchen …' });
  const meal = el('select', { class: 'select' });
  for (const m of MEAL_TYPES) {
    const option = el('option', { value: m.key }, `${m.icon} ${m.label}`);
    if (m.key === 'abendessen') option.selected = true;
    meal.append(option);
  }

  const results = el('div', { class: 'results' });
  const body = el('div', {}, [
    el('div', { class: 'field' }, [el('label', {}, 'Mahlzeit'), meal]),
    el('div', { class: 'search' }, [icon('search', { size: 18 }), search]),
    results,
  ]);

  const sheet = openSheet(body, { title: 'Gericht einplanen' });

  const drawResults = () => {
    const q = search.value.trim().toLowerCase();
    const hits = recipes.filter((r) => !q || r.name.toLowerCase().includes(q));
    clear(results);
    if (!hits.length) {
      results.append(el('p', { class: 'tiny' }, 'Kein Rezept gefunden.'));
      return;
    }
    for (const r of hits) {
      results.append(
        el(
          'button',
          {
            class: 'result',
            type: 'button',
            onclick: guarded(
              async () => {
                await api('/api/plan', {
                  method: 'POST',
                  body: { date, recipeId: r.id, mealType: meal.value },
                });
                sheet.close();
                await load();
              },
              { success: 'Eingeplant' },
            ),
          },
          [
            el('div', { class: 'item-body' }, [
              el('div', { class: 'item-name' }, r.name),
              el('div', { class: 'item-meta' }, `${r.servings} Portionen`),
            ]),
            r.favorite ? icon('star', { size: 16 }) : null,
          ],
        ),
      );
    }
  };

  search.addEventListener('input', drawResults);
  drawResults();
}

/** Damit andere Bildschirme den Plan neu laden können. */
export function invalidatePlanCache(): void {
  recipes = [];
}

export { state };
